/**
 * Melco EXP.
 *
 * Headerless stream of 2-byte records: signed dx, dy (y axis up → negated). 0x80 escapes a
 * control record: 0x80 0x04 dx dy = jump, 0x80 0x01 0x00 0x00 = colour change/stop,
 * 0x80 0x80 0x07 0x00 = trim. A stitch delta of −128 (0x80) would be ambiguous, so the
 * displacement range is ±127. EXP stores no colours and no explicit end record.
 *
 * Reference: format notes from EmbroidePy/pyembroidery (MIT); independent implementation.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import { ByteReader, ByteWriter, FormatError } from './bytes';
import type { FormatLimits, MachinePattern, MachineStitch } from './machine';

export const EXP_LIMITS: FormatLimits = { maxStitch: 127, maxJump: 127, maxCoord: Number.MAX_SAFE_INTEGER };

function delta(v: number): number {
  if (!Number.isInteger(v) || v < -127 || v > 127) throw new FormatError(t('EXP displacement outside ±127: {v}', { v }));
  return v & 0xff;
}

export function writeExp(p: MachinePattern): Uint8Array {
  const w = new ByteWriter();
  let cx = 0, cy = 0;
  for (const s of p.stitches) {
    switch (s.cmd) {
      case CMD.STITCH:
        w.u8(delta(s.x - cx)).u8(delta(-(s.y - cy)));
        cx = s.x;
        cy = s.y;
        break;
      case CMD.JUMP:
        w.u8(0x80).u8(0x04).u8(delta(s.x - cx)).u8(delta(-(s.y - cy)));
        cx = s.x;
        cy = s.y;
        break;
      case CMD.TRIM:
        w.bytes([0x80, 0x80, 0x07, 0x00]);
        break;
      case CMD.COLOR_CHANGE:
      case CMD.STOP:
        w.bytes([0x80, 0x01, 0x00, 0x00]);
        break;
      case CMD.END:
        break;
    }
  }
  return w.toBytes();
}

export function readExp(bytes: Uint8Array): MachineStitch[] {
  if (bytes.length % 2 !== 0) throw new FormatError(t('The EXP file has an odd number of bytes.'));
  const r = new ByteReader(bytes);
  const out: MachineStitch[] = [];
  let x = 0, y = 0;
  while (r.remaining >= 2) {
    const b0 = r.u8();
    const b1 = r.u8();
    if (b0 !== 0x80) {
      x += b0 > 127 ? b0 - 256 : b0;
      y -= b1 > 127 ? b1 - 256 : b1;
      out.push({ x, y, cmd: CMD.STITCH });
      continue;
    }
    if (r.remaining < 2) throw new FormatError(t('Truncated EXP control record.'));
    const c0 = r.i8();
    const c1 = r.i8();
    if (b1 === 0x04) {
      x += c0;
      y -= c1;
      out.push({ x, y, cmd: CMD.JUMP });
    } else if (b1 === 0x01) {
      out.push({ x, y, cmd: CMD.COLOR_CHANGE });
    } else if (b1 === 0x80) {
      out.push({ x, y, cmd: CMD.TRIM });
    } else {
      throw new FormatError(t('Unknown EXP control code 0x{code}.', { code: b1.toString(16) }));
    }
  }
  out.push({ x, y, cmd: CMD.END });
  return out;
}
